// AddEditTransportModal.tsx - Complete rewrite with proper transport grouping
import React, { useState, useEffect } from "react";
import { Boxes, Layers, X } from "lucide-react";
import TransportForm from "../forms/TransportForm";

type Transport = {
  id?: string | number;
  parent_transport_id?: string;
  transport_group_id?: string;
  sequence_number?: number;
  is_main_transport?: boolean;
};

type AddEditTransportModalProps = {
  showAdd: boolean;
  showEdit: boolean;
  selectedTransport?: Transport | null;
  formData: any;
  setFormData: (data: any) => void;
  onClose: () => void;
  onSaveAdd: (submitData: any) => void;
  onSaveEdit: (submitData: any) => void;
};

const AddEditTransportModal: React.FC<AddEditTransportModalProps> = ({
  showAdd,
  showEdit,
  selectedTransport,
  formData,
  setFormData,
  onClose,
  onSaveAdd,
  onSaveEdit,
}) => {
  const [isFormValid, setIsFormValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!showAdd && !showEdit) return null;

  const handleClose = () => {
    if (isSubmitting) return; // Previne închiderea în timpul submit-ului
    console.log("Closing modal");
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isSubmitting) {
      console.log("Backdrop clicked - closing modal");
      onClose();
    }
  };

  const handleValidationChange = (isValid: boolean) => {
    setIsFormValid(isValid);
  };

  // Funcție helper pentru a elimina toate câmpurile ID problematice
  const cleanTransportData = (data: any) => {
    const cleanData = { ...data };
    
    // Lista de câmpuri care trebuie eliminate pentru transporturi noi
    const fieldsToRemove = [
      'id',
      '_id', 
      'transport_id',
      'shipment_id',
      'expediere_id',
      'multiple_destinations',
      'create_separate_transports'
    ];
    
    fieldsToRemove.forEach(field => {
      delete cleanData[field];
    });
    
    // Elimină orice câmp care se termină cu '_id' dar păstrează cele necesare
    Object.keys(cleanData).forEach(key => {
      if (key.endsWith('_id') && 
          !['client_id', 'sofer_id', 'vehicul_id', 'locatie_plecare_id', 'locatie_destinatie_id', 'parent_transport_id'].includes(key)) {
        delete cleanData[key];
      }
    });
    
    return cleanData;
  };

  // Funcție pentru generarea unui ID de grup unic
  const generateGroupId = () => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substr(2, 9);
    return `GRP_${timestamp}_${random}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isSubmitting) return; // Previne submit-uri multiple
    
    console.log("Form submitted");
    console.log("Form data before submit:", formData);

    setIsSubmitting(true);

    try {
      if (showAdd) {
        // Verifică dacă trebuie să creeze transporturi separate
        if (formData.create_separate_transports && 
            formData.multiple_destinations && 
            formData.multiple_destinations.length > 0) {
          
          await handleMultipleTransportsCreation();
          
        } else {
          // Transport normal sau cu multiple destinații dar fără separare
          await handleSingleTransportCreation();
        }
      } else if (showEdit && selectedTransport?.id) {
        // Pentru editare, nu permitem crearea de transporturi multiple
        await handleTransportEdit();
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      alert(`Eroare la procesarea transportului: ${error instanceof Error ? error.message : 'Verifică consola pentru detalii.'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMultipleTransportsCreation = async () => {
    const createdTransports = [];
    const groupId = generateGroupId();
    
    console.log(`Starting creation of ${formData.multiple_destinations.length + 1} separate transports with group ID: ${groupId}`);
    
    try {
      // 1. Creează transport principal
      const mainTransportData = {
        ...cleanTransportData(formData),
        transport_group_id: groupId,
        parent_transport_id: null,
        is_main_transport: true,
        sequence_number: 1
      };
      
      console.log("Creating main transport:", mainTransportData);
      const mainTransport = await onSaveAdd(mainTransportData);
      createdTransports.push(mainTransport);
      
      console.log("Main transport created with ID:", mainTransport?.id);

      // 2. Creează transporturi pentru destinațiile suplimentare
      for (let i = 0; i < formData.multiple_destinations.length; i++) {
        const destination = formData.multiple_destinations[i];
        
        const additionalTransportData = {
          // Copiază toate datele de bază din formData
          client: formData.client,
          client_id: formData.client_id,
          plecare_separata: formData.plecare_separata,
          adresa_plecare: formData.adresa_plecare,
          oras_plecare: formData.oras_plecare,
          locatie_plecare_id: formData.locatie_plecare_id,
          status: formData.status,
          prioritate: formData.prioritate,
          value: formData.value,
          tip_plata: formData.tip_plata,
          sofer_id: formData.sofer_id,
          vehicul_id: formData.vehicul_id,
          ora_plecarii: formData.ora_plecarii,
          ora_estimata: formData.ora_estimata,
          progres: formData.progres || 0,
          
          // Setează destinația specifică
          destinatie: destination.destinatie,
          oras_destinatie: destination.oras_destinatie,
          locatie_destinatie_id: destination.locatie_destinatie_id,
          
          // Setează contactele - TOATE câmpurile de contact
         
          nume_expeditor: formData.nume_expeditor || '', // Nume expeditor dacă există
          telefon_destinatar: destination.telefon_destinatar || formData.telefon_destinatar || '',
          nume_destinatar: destination.nume_destinatar || formData.nume_destinatar || '',
          
          // Combină descrierile
          descriere: [
            formData.descriere || '',
            `Destinația ${i + 2} din ${formData.multiple_destinations.length + 1} destinații totale`,
            destination.descriere ? `Detalii specifice: ${destination.descriere}` : ''
          ].filter(Boolean).join('\n\n'),
          
          // Câmpuri pentru grupare
          parent_transport_id: mainTransport?.id || null,
          transport_group_id: groupId,
          is_main_transport: false,
          sequence_number: i + 2
        };
        
        // Curăță datele pentru a elimina ID-uri problematice
        const cleanedData = cleanTransportData(additionalTransportData);
        
        console.log(`Creating additional transport ${i + 1}:`, cleanedData);
        
        try {
          const additionalTransport = await onSaveAdd(cleanedData);
          createdTransports.push(additionalTransport);
          console.log(`Additional transport ${i + 1} created with ID:`, additionalTransport?.id);
        } catch (error) {
          console.error(`Error creating additional transport ${i + 1}:`, error);
          throw new Error(`Eroare la crearea transportului suplimentar ${i + 1}: ${error}`);
        }
      }
      
   
      
      alert(`S-au creat cu succes ${createdTransports.length} transporturi separate!`);
      
   ;
      
    } catch (error) {
      console.error("Error in multiple transports creation:", error);
      throw error;
    }
  };

  const handleSingleTransportCreation = async () => {
    // Transport normal sau cu multiple destinații dar fără separare
    const submitData = cleanTransportData(formData);
    
    // Pentru transport singular, setează valorile implicite pentru grupare
    submitData.transport_group_id = generateGroupId();
    submitData.parent_transport_id = null;
    submitData.is_main_transport = true;
    submitData.sequence_number = 1;
    
   
    console.log("Creating single transport:", submitData);
    await onSaveAdd(submitData);
  };

  const handleTransportEdit = async () => {
    
    const submitData = {
      ...formData,
      id: selectedTransport?.id,
      transport_group_id: selectedTransport?.transport_group_id || null,
      parent_transport_id: selectedTransport?.parent_transport_id || null,
      is_main_transport: selectedTransport?.is_main_transport || false,
      sequence_number: selectedTransport?.sequence_number || 1,
    };

    // Elimină orice câmp care poate provoca crearea de transporturi multiple
    delete submitData.multiple_destinations;
    delete submitData.create_separate_transports;

   

    console.log("Editing transport:", submitData);
    await onSaveEdit(submitData);
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50 p-4 bg-black/50 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div className="rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden relative shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white p-6 rounded-t-2xl">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center overflow-hidden">
                <Layers className="w-8 h-8 text-orange-600" />
              </div>
              <div>
                <p className="text-xl font-semibold">
                  {showAdd ? "Adaugă Transport" : "Editează Transport"}
                </p>
                <p className="text-sm text-white/80">
                  {showAdd 
                    ? "Completează detaliile cursei în formularul de mai jos" 
                    : `Modifică detaliile pentru transportul ${selectedTransport?.id}`
                  }
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              className="text-white hover:bg-red-600 p-2 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              type="button"
              aria-label="Închide modal"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Conținutul formularului */}
        <div className="bg-white p-6 overflow-y-auto max-h-[75vh] rounded-b-2xl">
          {isSubmitting && (
            <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10 rounded-b-2xl">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto mb-4"></div>
                <p className="text-gray-600">Se procesează transporturile...</p>
              </div>
            </div>
          )}
          
          <form onSubmit={handleSubmit}>
            <TransportForm 
              formData={formData} 
              setFormData={setFormData}
              onValidationChange={handleValidationChange}
              isEditMode={showEdit}
              transportId={selectedTransport?.id?.toString()}
            />

            {/* Butoane formular */}
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Anulează
              </button>
              <button
                type="submit"
                disabled={!isFormValid || isSubmitting}
                className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Procesează...
                  </>
                ) : (
                  showAdd ? "Salvează" : "Salvează Modificările"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddEditTransportModal;